import type { TSchema } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import {
  type BodyOf,
  DISCORD_ACCESS_MESSAGES,
  encodeParams,
  fillPath,
  type ParamsOf,
  type QueryOf,
  ROUTES,
  type RouteById,
  type RouteId,
  type SuccessOf,
  toQueryString,
} from "@volgo/core/contract";
import { ErrorBody, type ErrorCode } from "@volgo/core/errors";

/**
 * THE ONLY PLACE THE SPA TALKS TO THE API (spec 9). Screens call `client.call("catalog.list", { query })`
 * and get the contract's types back; nothing else in apps/web uses fetch.
 *
 * - Access token: kept in memory, sent as `Authorization: Bearer`.
 * - Refresh: a 401 on a login-protected route triggers ONE `auth.refresh` (shared by concurrent calls) and
 *   ONE retry of the call. A second 401 is an error, never a loop. Only an authoritative 401/403 refresh rejection loses the session.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: ErrorCode | "NETWORK" | "UNKNOWN";
  readonly details: Record<string, unknown> | undefined;

  constructor(status: number, code: ApiError["code"], message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Routes a script can call: not /internal, and not the redirect routes (those are browser navigations). */
export type CallableId = {
  [K in RouteId]: RouteById<K>["access"] extends "internal"
    ? never
    : 302 extends keyof RouteById<K>["responses"]
      ? never
      : K;
}[RouteId];

type WithParams<R> = R extends { params: TSchema } ? { params: ParamsOf<R> } : { params?: undefined };
type WithQuery<R> = R extends { query: TSchema } ? { query?: QueryOf<R> } : { query?: undefined };
type WithBody<R> = R extends { body: TSchema } ? { body: BodyOf<R> } : { body?: undefined };
export type CallInput<K extends RouteId> = WithParams<RouteById<K>> &
  WithQuery<RouteById<K>> &
  WithBody<RouteById<K>> & { signal?: AbortSignal };
type NeedsInput<K extends RouteId> =
  RouteById<K> extends { params: TSchema } | { body: TSchema } ? true : false;
type CallArgs<K extends RouteId> =
  NeedsInput<K> extends true ? [input: CallInput<K>] : [input?: CallInput<K>];

export function isAccessRejection(status: number): boolean {
  return status === 401 || status === 403;
}

export function isSessionRejection(error: unknown): boolean {
  return error instanceof ApiError && isAccessRejection(error.status);
}

export interface ApiClientOptions {
  /** origin of the API; empty = same origin (the SPA is served by the same host) */
  baseUrl?: string;
  fetch?: (input: string, init?: RequestInit) => Promise<Response>;
  /** called when refresh authoritatively rejects the session (401/403) */
  onSessionLost?: () => void;
}

export interface ApiClient {
  media(
    url: string,
    uploadToken: string,
    method: "HEAD" | "PATCH" | "DELETE",
    offset?: number,
    body?: Blob,
    signal?: AbortSignal,
  ): Promise<Response>;
  saveVideoPosition(id: string, positionMs: number): Promise<void>;
  seriesImage(id: string, image: Blob): Promise<void>;
  call<K extends CallableId>(id: K, ...args: CallArgs<K>): Promise<SuccessOf<RouteById<K>>>;
  /** current access token (memory only) */
  readonly accessToken: string | null;
  /** advances on every credential issuance, even when the JWT string is unchanged */
  readonly credentialGeneration: number;
  /** Allowlisted reason from the current session's authoritative Discord refresh rejection. */
  readonly discordRejection: "not_member" | "missing_role" | null;
  /** refresh cookies once for the credentials used by a failed image or API request */
  refreshSession(generation: number): Promise<boolean>;
  /** subscribe to login/logout; returns an unsubscribe function */
  onTokenChange(listener: (token: string | null) => void): () => void;
}

export function createApiClient(options: ApiClientOptions = {}): ApiClient {
  const baseUrl = (options.baseUrl ?? "").replace(/\/+$/, "");
  const doFetch = options.fetch ?? ((input: string, init?: RequestInit) => fetch(input, init));
  let token: string | null = null;
  let generation = 0;
  let sessionStart = 0;
  let discordRejection: ApiClient["discordRejection"] = null;
  let refreshing: Promise<SuccessOf<RouteById<"auth.refresh">>> | null = null;
  let refreshingSession = 0;
  let authResponse: Promise<unknown> = Promise.resolve();
  const listeners = new Set<(t: string | null) => void>();

  const setToken = (t: string | null) => {
    if (t === token) return;
    token = t;
    for (const l of listeners) l(t);
  };

  async function send(
    id: RouteId,
    input: { params?: object; query?: object; body?: unknown; signal?: AbortSignal } | undefined,
  ): Promise<Response> {
    const spec = ROUTES[id] as {
      method: string;
      path: string;
      access: string;
      query?: TSchema;
      body?: TSchema;
    };
    const q = toQueryString(encodeParams(spec.query, input?.query as Record<string, unknown> | undefined));
    const url = `${baseUrl}${fillPath(spec.path, input?.params as Record<string, unknown> | undefined)}${q ? `?${q}` : ""}`;
    const headers: Record<string, string> = { accept: "application/json" };
    // public routes (login, refresh, logout) never carry a bearer: a stale one must not break them
    if (token && spec.access !== "public") headers.authorization = `Bearer ${token}`;
    const init: RequestInit = { method: spec.method, headers, credentials: "include", signal: input?.signal };
    if (spec.body && input?.body !== undefined) {
      headers["content-type"] = "application/json";
      init.body = JSON.stringify(input.body);
    }
    try {
      // Set-Cookie must arrive in order, including refresh racing with logout or a new login.
      if (id === "auth.refresh" || id === "auth.keyLogin" || id === "auth.logout") {
        const response = authResponse.catch(() => {}).then(() => doFetch(url, init));
        authResponse = response;
        return await response;
      }
      return await doFetch(url, init);
    } catch (e) {
      throw new ApiError(0, "NETWORK", e instanceof Error ? e.message : "network error");
    }
  }

  async function parse(res: Response): Promise<unknown> {
    if (res.ok) return res.status === 204 ? null : res.json();
    let body: unknown;
    try {
      body = await res.json();
    } catch {
      body = undefined;
    }
    if (Value.Check(ErrorBody, body)) {
      throw new ApiError(res.status, body.error.code, body.error.message, body.error.details);
    }
    throw new ApiError(res.status, "UNKNOWN", `HTTP ${res.status}`);
  }

  /** Concurrent callers in the same login session share one refresh. */
  function refresh(): Promise<SuccessOf<RouteById<"auth.refresh">>> {
    if (refreshing && refreshingSession === sessionStart) return refreshing;
    const pending: Promise<SuccessOf<RouteById<"auth.refresh">>> = (async () => {
      const started = generation;
      try {
        const res = await send("auth.refresh", undefined);
        const session = (await parse(res)) as SuccessOf<RouteById<"auth.refresh">>;
        if (started !== generation) throw new ApiError(401, "TOKEN_EXPIRED", "Session changed");
        generation++;
        discordRejection = null;
        setToken(session.accessToken);
        return session;
      } catch (error) {
        if (started === generation && isSessionRejection(error)) {
          sessionStart = ++generation;
          discordRejection =
            error instanceof ApiError && error.code === "FORBIDDEN"
              ? ((["not_member", "missing_role"] as const).find(
                  (reason) => DISCORD_ACCESS_MESSAGES[reason] === error.message,
                ) ?? null)
              : null;
          setToken(null);
          options.onSessionLost?.();
        }
        throw error;
      }
    })().finally(() => {
      if (refreshing === pending) refreshing = null;
    });
    refreshingSession = sessionStart;
    refreshing = pending;
    return pending;
  }

  async function refreshSession(usedGeneration: number): Promise<boolean> {
    if (usedGeneration < sessionStart) return false;
    if (usedGeneration !== generation) return token !== null;
    try {
      await refresh();
      return usedGeneration >= sessionStart && token !== null;
    } catch {
      return false;
    }
  }

  const client: ApiClient = {
    media(url, uploadToken, method, offset, body, signal) {
      return doFetch(url, {
        method,
        headers: {
          authorization: `Bearer ${uploadToken}`,
          ...(offset === undefined
            ? {}
            : { "Upload-Offset": String(offset), "content-type": "application/octet-stream" }),
        },
        body,
        signal,
      });
    },
    async saveVideoPosition(id, positionMs) {
      if (!token) return;
      await doFetch(`${baseUrl}/api/videos/${encodeURIComponent(id)}/progress`, {
        method: "PUT",
        credentials: "include",
        keepalive: true,
        headers: { authorization: `Bearer ${token}`, "content-type": "application/json" },
        body: JSON.stringify({ positionMs }),
      });
    },
    async seriesImage(id, image) {
      const used = generation;
      const sendImage = () =>
        doFetch(`${baseUrl}/api/video-series/${encodeURIComponent(id)}/key-visual`, {
          method: "PUT",
          credentials: "include",
          headers: { authorization: `Bearer ${token}`, "content-type": "image/jpeg" },
          body: image,
        });
      let response = await sendImage();
      if (response.status === 401 && (await refreshSession(used))) response = await sendImage();
      await parse(response);
    },
    async call(id, ...args) {
      if (id === "auth.refresh") return (await refresh()) as never;
      const input = args[0] as { params?: object; query?: object; body?: unknown } | undefined;
      const spec = ROUTES[id];
      const usedGeneration = generation;
      let res = await send(id, input);
      // expired or missing access token on a protected route: refresh once, retry once
      if (res.status === 401 && spec.access !== "public" && usedGeneration >= sessionStart) {
        if (usedGeneration === generation) {
          try {
            await refresh();
          } catch (error) {
            // Preserve the original missing/expired-token response, but surface outages and role loss.
            if (!(error instanceof ApiError && error.status === 401)) throw error;
          }
        }
        if (usedGeneration >= sessionStart && token !== null) res = await send(id, input);
      }
      const data = await parse(res);
      if (id === "auth.keyLogin") {
        sessionStart = ++generation;
        discordRejection = null;
        setToken((data as { accessToken: string }).accessToken);
      }
      if (id === "auth.logout") {
        sessionStart = ++generation;
        discordRejection = null;
        setToken(null);
      }
      return data as never;
    },
    get accessToken() {
      return token;
    },
    get credentialGeneration() {
      return generation;
    },
    get discordRejection() {
      return discordRejection;
    },
    refreshSession,
    onTokenChange(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
  };
  return client;
}
